import { CountryCodes } from "@/utils/country-codes";
import type { iBrand, iBrandQuoteLegalConfig } from "./brand-model";

export interface ResolvedBrandPostalAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface ResolvedBrandIdentity {
  logoUrl: string;
  displayName: string;
  legalName: string;
  atolNumber: string;
  website: string;
  countryDisplay: string;
  postalAddress: ResolvedBrandPostalAddress;
  termsAndConditionsUrl: string;
  bookingConditionsUrl: string;
}

export interface ResolvedBrandReceiptHeader {
  logoUrl: string;
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  website: string;
}

function trimOrEmpty(value: string | undefined | null): string {
  return String(value ?? "").trim();
}

function firstNonEmpty(...values: Array<string | undefined | null>): string {
  for (const value of values) {
    const trimmed = trimOrEmpty(value);
    if (trimmed) return trimmed;
  }
  return "";
}

function countryCodeToName(code: string | undefined | null): string {
  const trimmed = String(code ?? "").trim();
  if (!trimmed) return "";

  if (trimmed.length === 2) {
    const match = CountryCodes.find(
      (entry) => entry.code.toUpperCase() === trimmed.toUpperCase()
    );
    if (match?.name) return match.name;
  }

  return trimmed;
}

function resolveCountryDisplay(brand: iBrand): string {
  const fromIso = countryCodeToName(brand.country);
  if (fromIso) return fromIso;
  return trimOrEmpty(brand.customerReceiptTemplate?.country);
}

function resolvePostalAddress(brand: iBrand): ResolvedBrandPostalAddress {
  const legacy = brand.customerReceiptTemplate;
  const postal = brand.postalAddress;

  return {
    line1: firstNonEmpty(postal?.line1, legacy?.addressLine1),
    line2: firstNonEmpty(postal?.line2, legacy?.addressLine2),
    city: firstNonEmpty(postal?.city, legacy?.city),
    state: firstNonEmpty(postal?.state, legacy?.state),
    postalCode: firstNonEmpty(postal?.postalCode, legacy?.postalCode),
  };
}

export function resolveBrandIdentity(brand: iBrand): ResolvedBrandIdentity {
  const legacyReceipt = brand.customerReceiptTemplate;
  const legacyLegal = brand.quoteLegalConfig;
  const legacyAtol = brand.ATOLCertificateConfig;
  const displayName = trimOrEmpty(brand.name);
  const postalAddress = resolvePostalAddress(brand);

  return {
    logoUrl: firstNonEmpty(brand.logo, legacyReceipt?.logoUrl),
    displayName,
    legalName: firstNonEmpty(
      brand.legalName,
      legacyAtol?.legalName,
      legacyReceipt?.companyName,
      brand.name
    ),
    atolNumber: firstNonEmpty(brand.atolNumber, legacyAtol?.atolNumber),
    website: firstNonEmpty(brand.website, legacyReceipt?.website),
    countryDisplay: resolveCountryDisplay(brand),
    postalAddress,
    termsAndConditionsUrl: firstNonEmpty(
      brand.termsAndConditionsUrl,
      legacyLegal?.termsAndConditionsUrl
    ),
    bookingConditionsUrl: firstNonEmpty(
      brand.bookingConditionsUrl,
      legacyLegal?.bookingConditionsUrl
    ),
  };
}

export function resolveBrandReceiptHeader(brand: iBrand): ResolvedBrandReceiptHeader {
  const identity = resolveBrandIdentity(brand);

  return {
    logoUrl: identity.logoUrl,
    companyName: identity.legalName,
    addressLine1: identity.postalAddress.line1,
    addressLine2: identity.postalAddress.line2,
    city: identity.postalAddress.city,
    state: identity.postalAddress.state,
    postalCode: identity.postalAddress.postalCode,
    country: identity.countryDisplay,
    website: identity.website,
  };
}

export function resolveBrandQuoteLegalConfig(brand: iBrand): iBrandQuoteLegalConfig {
  const identity = resolveBrandIdentity(brand);
  return {
    termsAndConditionsUrl: identity.termsAndConditionsUrl || undefined,
    bookingConditionsUrl: identity.bookingConditionsUrl || undefined,
  };
}

export function resolvePublicBrandQuoteLegalConfig(brandPublicData: {
  termsAndConditionsUrl?: string;
  bookingConditionsUrl?: string;
  quoteLegalConfig?: iBrandQuoteLegalConfig;
}): iBrandQuoteLegalConfig {
  return {
    termsAndConditionsUrl:
      brandPublicData.termsAndConditionsUrl?.trim() ||
      brandPublicData.quoteLegalConfig?.termsAndConditionsUrl,
    bookingConditionsUrl:
      brandPublicData.bookingConditionsUrl?.trim() ||
      brandPublicData.quoteLegalConfig?.bookingConditionsUrl,
  };
}
